import React, { useState, useEffect } from 'react';
import apiService, { TableSchema } from '../services/api';
import './TableManager.css';
import { useAuth } from '../contexts/AuthContext';

interface TableManagerProps {
  domainId: string;
  schema: string;
  tableName: string;
  displayName: string;
  hiddenColumns?: string[];
  currentEnvironment?: string;
}

const TableManager: React.FC<TableManagerProps> = ({ domainId, schema, tableName, displayName, hiddenColumns = [], currentEnvironment }) => {
  const [allData, setAllData] = useState<any[]>([]); // All records from database
  const [filteredData, setFilteredData] = useState<any[]>([]); // After filter/sort
  const [columns, setColumns] = useState<TableSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [editingRow, setEditingRow] = useState<any>(null);
  const [newRow, setNewRow] = useState<any>({});
  const [showNewRow, setShowNewRow] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterColumn, setFilterColumn] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [surveys, setSurveys] = useState<any[]>([]);
  const [visitTypes, setVisitTypes] = useState<any[]>([]);
  const pageSize = 50;

  // Get permission checks from auth context
  const { canModifyEnvironment, canPerformOperation } = useAuth();
  const canModify = currentEnvironment ? canModifyEnvironment(domainId, currentEnvironment as any) : false;

  // Use default hidden columns if none provided
  const defaultHiddenColumns = ['created', 'createdby', 'updated', 'updatedby'];
  const actualHiddenColumns = hiddenColumns.length > 0 ? hiddenColumns : defaultHiddenColumns;

  useEffect(() => {
    fetchSchema();
    fetchAllData();
    fetchCount();
    // Fetch surveys if this is the AllSearchTerms table
    if (schema === 'phynd' && tableName === 'AllSearchTerms') {
      fetchSurveys();
    }
    // Fetch visit types if this is the VisitTypeReason table
    if (schema === 'phynd' && tableName === 'VisitTypeReason') {
      fetchVisitTypes();
    }
  }, [schema, tableName, currentEnvironment]);

  useEffect(() => {
    let processedData = [...allData];

    // Apply search filter
    if (searchTerm) {
      processedData = processedData.filter(row => {
        if (filterColumn === 'all') {
          // Search across all visible columns
          return columns.some(col => {
            const value = row[col.COLUMN_NAME];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(searchTerm.toLowerCase());
          });
        } else {
          // Search in specific column
          const value = row[filterColumn];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        }
      });
    }

    // Apply sorting
    if (sortColumn) {
      processedData.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
        if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? -1 : 1;

        // Compare values
        let comparison = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredData(processedData);

    // Reset to first page when search or sort changes
    if (searchTerm !== '') {
      setCurrentPage(0);
    }
  }, [searchTerm, filterColumn, allData, sortColumn, sortDirection, columns]);

  const fetchSchema = async () => {
    try {
      const schemaData = await apiService.getTableSchema(schema, tableName);
      // Filter out hidden columns (case-insensitive)
      let visibleColumns = schemaData.filter(
        col => !actualHiddenColumns.map(h => h.toLowerCase()).includes(col.COLUMN_NAME.toLowerCase())
      );

      // Custom column ordering for workflow_steps table
      if (schema === 'SchEng' && tableName === 'workflow_steps') {
        // Reorder columns to put step_order before step_name
        visibleColumns.sort((a, b) => {
          const aCol = a.COLUMN_NAME;
          const bCol = b.COLUMN_NAME;

          // Define desired order
          const order = ['id', 'workflow_id', 'step_order', 'step_name'];

          const aIndex = order.indexOf(aCol);
          const bIndex = order.indexOf(bCol);

          // If both columns are in the order array, sort by their index
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }

          // If only one is in the order array, it comes first
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;

          // If neither is in the order array, maintain original order
          return 0;
        });
      }

      // Add SurveyTitle column for AllSearchTerms table
      if (schema === 'phynd' && tableName === 'AllSearchTerms') {
        // Add virtual column for Survey Title after FormId
        const formIdIndex = visibleColumns.findIndex(col => col.COLUMN_NAME === 'FormId');
        if (formIdIndex !== -1) {
          visibleColumns.splice(formIdIndex + 1, 0, {
            COLUMN_NAME: 'SurveyTitle',
            DATA_TYPE: 'varchar',
            IS_NULLABLE: 'YES',
            CHARACTER_MAXIMUM_LENGTH: null,
            NUMERIC_PRECISION: null,
            NUMERIC_SCALE: null,
            COLUMN_DEFAULT: null,
            IS_PRIMARY_KEY: 'NO',
            IS_IDENTITY: 0
          });
        }
      }

      // Add VisitTypeDisplayName column for VisitTypeReason table
      if (schema === 'phynd' && tableName === 'VisitTypeReason') {
        // Add virtual column for VisitType Display Name after VisitTypeId
        const visitTypeIdIndex = visibleColumns.findIndex(col => col.COLUMN_NAME === 'VisitTypeId');
        if (visitTypeIdIndex !== -1) {
          visibleColumns.splice(visitTypeIdIndex + 1, 0, {
            COLUMN_NAME: 'VisitTypeDisplayName',
            DATA_TYPE: 'varchar',
            IS_NULLABLE: 'YES',
            CHARACTER_MAXIMUM_LENGTH: null,
            NUMERIC_PRECISION: null,
            NUMERIC_SCALE: null,
            COLUMN_DEFAULT: null,
            IS_PRIMARY_KEY: 'NO',
            IS_IDENTITY: 0
          });
        }
      }

      setColumns(visibleColumns);
    } catch (err) {
      setError('Failed to fetch schema: ' + (err as Error).message);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all records (or a large limit)
      const result = await apiService.getTableData(schema, tableName, 10000, 0);
      setAllData(result);
      setFilteredData(result);
    } catch (err) {
      setError('Failed to fetch data: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const result = await apiService.getTableCount(schema, tableName);
      setTotalCount(result.count);
    } catch (err) {
      console.error('Failed to fetch count:', err);
    }
  };

  const fetchSurveys = async () => {
    try {
      const surveyData = await apiService.getSurveys();
      // Filter surveys to only include those starting with "RFV"
      const rfvSurveys = surveyData.filter((survey: any) =>
        survey && survey.Title && typeof survey.Title === 'string' && survey.Title.startsWith('RFV')
      );
      setSurveys(rfvSurveys);
    } catch (err) {
      console.error('Failed to fetch surveys:', err);
    }
  };

  const fetchVisitTypes = async () => {
    try {
      const visitTypeData = await apiService.getVisitTypes();
      setVisitTypes(visitTypeData);
    } catch (err) {
      console.error('Failed to fetch visit types:', err);
    }
  };

  const getPrimaryKeyColumns = () => {
    return columns.filter(col => col.IS_PRIMARY_KEY === 'YES');
  };

  const getPrimaryKey = (row: any) => {
    const pkColumns = getPrimaryKeyColumns();
    const pk: any = {};
    pkColumns.forEach(col => {
      pk[col.COLUMN_NAME] = row[col.COLUMN_NAME];
    });
    return pk;
  };

  const handleCreate = async () => {
    try {
      const dataToSend: any = {};
      columns.forEach(col => {
        // Skip virtual columns like SurveyTitle and VisitTypeDisplayName
        if (col.COLUMN_NAME === 'SurveyTitle' || col.COLUMN_NAME === 'VisitTypeDisplayName') return;

        if (col.IS_IDENTITY === 0) {
          const value = newRow[col.COLUMN_NAME];
          // Only send non-empty values, let database handle NULL for empty strings
          if (value !== undefined && value !== '') {
            dataToSend[col.COLUMN_NAME] = value;
          }
          // Empty strings will be omitted, allowing database to set NULL
        }
      });

      // Include hidden columns with default values if needed
      actualHiddenColumns.forEach(col => {
        if (col === 'CreatedBy' || col === 'UpdatedBy') {
          dataToSend[col] = 'Admin'; // Default user
        }
        // Created and Updated will be set by database defaults
      });

      await apiService.createRecord(schema, tableName, dataToSend);
      fetchAllData();
      fetchCount();
      setNewRow({});
      setShowNewRow(false);
      setSearchTerm('');
      setError('');
    } catch (err) {
      setError('Failed to create: ' + (err as Error).message);
    }
  };

  const handleUpdate = async (row: any) => {
    try {
      const pk = getPrimaryKey(row);
      const dataToUpdate: any = {};

      columns.forEach(col => {
        // Skip virtual columns like SurveyTitle and VisitTypeDisplayName
        if (col.COLUMN_NAME === 'SurveyTitle' || col.COLUMN_NAME === 'VisitTypeDisplayName') return;

        if (col.IS_PRIMARY_KEY !== 'YES' && col.IS_IDENTITY === 0) {
          const value = row[col.COLUMN_NAME];
          // Send NULL for empty strings, actual values otherwise
          if (value === '') {
            dataToUpdate[col.COLUMN_NAME] = null;
          } else {
            dataToUpdate[col.COLUMN_NAME] = value;
          }
        }
      });

      // Include UpdatedBy for audit trail if it's a hidden column
      if (actualHiddenColumns.some(h => h.toLowerCase() === 'updatedby')) {
        dataToUpdate['UpdatedBy'] = 'Admin'; // Default user
      }
      // Updated will be set by database trigger/default

      await apiService.updateRecord(schema, tableName, pk, dataToUpdate);
      fetchAllData();
      setEditingRow(null);
      setError('');
    } catch (err) {
      setError('Failed to update: ' + (err as Error).message);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const pk = getPrimaryKey(row);
      await apiService.deleteRecord(schema, tableName, pk);
      fetchAllData();
      fetchCount();
      setError('');
    } catch (err) {
      setError('Failed to delete: ' + (err as Error).message);
    }
  };

  const handleInputChange = (row: any, columnName: string, value: any) => {
    if (editingRow && getPrimaryKeyString(editingRow) === getPrimaryKeyString(row)) {
      // Store empty string as is in state (will convert to NULL when saving)
      setEditingRow({ ...editingRow, [columnName]: value });
    }
  };

  const handleNewRowChange = (columnName: string, value: any) => {
    setNewRow({ ...newRow, [columnName]: value });
  };

  const getPrimaryKeyString = (row: any) => {
    const pk = getPrimaryKey(row);
    return JSON.stringify(pk);
  };

  const renderCellValue = (row: any, column: TableSchema) => {
    const value = row[column.COLUMN_NAME];
    const isEditing = editingRow && getPrimaryKeyString(editingRow) === getPrimaryKeyString(row);

    // Special handling for SurveyTitle (display only, not editable)
    if (column.COLUMN_NAME === 'SurveyTitle') {
      return value !== null && value !== undefined ? String(value) : '';
    }

    // Special handling for VisitTypeDisplayName (display only, not editable)
    if (column.COLUMN_NAME === 'VisitTypeDisplayName') {
      return value !== null && value !== undefined ? String(value) : '';
    }

    if (isEditing && column.IS_PRIMARY_KEY !== 'YES' && column.IS_IDENTITY === 0) {
      // Special handling for FormId in AllSearchTerms table
      if (schema === 'phynd' && tableName === 'AllSearchTerms' && column.COLUMN_NAME === 'FormId') {
        return (
          <select
            value={editingRow[column.COLUMN_NAME] ?? ''}
            onChange={(e) => handleInputChange(row, column.COLUMN_NAME, e.target.value ? parseInt(e.target.value) : null)}
            className="table-input"
          >
            <option value="">-- Select Survey --</option>
            {surveys.map(survey => (
              <option key={survey.Id} value={survey.Id}>
                {survey.Id} - {survey.Title}
              </option>
            ))}
          </select>
        );
      }

      // Special handling for VisitTypeId in VisitTypeReason table
      if (schema === 'phynd' && tableName === 'VisitTypeReason' && column.COLUMN_NAME === 'VisitTypeId') {
        return (
          <select
            value={editingRow[column.COLUMN_NAME] ?? ''}
            onChange={(e) => handleInputChange(row, column.COLUMN_NAME, e.target.value ? parseInt(e.target.value) : null)}
            className="table-input"
          >
            <option value="">-- Select Visit Type --</option>
            {visitTypes.map(visitType => (
              <option key={visitType.VisitTypeId} value={visitType.VisitTypeId}>
                {visitType.VisitTypeId} - {visitType.VisitTypeDisplayName}
              </option>
            ))}
          </select>
        );
      }

      return (
        <input
          type={getInputType(column.DATA_TYPE)}
          value={editingRow[column.COLUMN_NAME] ?? ''}
          onChange={(e) => handleInputChange(row, column.COLUMN_NAME, e.target.value)}
          className="table-input"
        />
      );
    }

    // Display empty string for NULL values
    return value !== null && value !== undefined ? String(value) : '';
  };

  const renderNewRowCell = (column: TableSchema) => {
    if (column.IS_IDENTITY === 1) {
      return <span className="auto-generated">Auto</span>;
    }

    // Special handling for SurveyTitle (read-only)
    if (column.COLUMN_NAME === 'SurveyTitle') {
      const formId = newRow.FormId;
      const survey = surveys.find(s => s.Id === formId);
      return <span>{survey ? survey.Title : ''}</span>;
    }

    // Special handling for VisitTypeDisplayName (read-only)
    if (column.COLUMN_NAME === 'VisitTypeDisplayName') {
      const visitTypeId = newRow.VisitTypeId;
      const visitType = visitTypes.find(vt => vt.VisitTypeId === visitTypeId);
      return <span>{visitType ? visitType.VisitTypeDisplayName : ''}</span>;
    }

    // Special handling for FormId in AllSearchTerms table
    if (schema === 'phynd' && tableName === 'AllSearchTerms' && column.COLUMN_NAME === 'FormId') {
      return (
        <select
          value={newRow[column.COLUMN_NAME] ?? ''}
          onChange={(e) => handleNewRowChange(column.COLUMN_NAME, e.target.value ? parseInt(e.target.value) : null)}
          className="table-input"
        >
          <option value="">-- Select Survey --</option>
          {surveys.map(survey => (
            <option key={survey.Id} value={survey.Id}>
              {survey.Id} - {survey.Title}
            </option>
          ))}
        </select>
      );
    }

    // Special handling for VisitTypeId in VisitTypeReason table
    if (schema === 'phynd' && tableName === 'VisitTypeReason' && column.COLUMN_NAME === 'VisitTypeId') {
      return (
        <select
          value={newRow[column.COLUMN_NAME] ?? ''}
          onChange={(e) => handleNewRowChange(column.COLUMN_NAME, e.target.value ? parseInt(e.target.value) : null)}
          className="table-input"
        >
          <option value="">-- Select Visit Type --</option>
          {visitTypes.map(visitType => (
            <option key={visitType.VisitTypeId} value={visitType.VisitTypeId}>
              {visitType.VisitTypeId} - {visitType.VisitTypeDisplayName}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={getInputType(column.DATA_TYPE)}
        value={newRow[column.COLUMN_NAME] ?? ''}
        onChange={(e) => handleNewRowChange(column.COLUMN_NAME, e.target.value)}
        placeholder={column.IS_NULLABLE === 'YES' ? 'Optional' : 'Required'}
        className="table-input"
      />
    );
  };

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and reset to ascending
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (columnName: string) => {
    if (sortColumn !== columnName) {
      return ' ↕'; // Unsorted indicator
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const getInputType = (dataType: string) => {
    if (dataType.includes('int') || dataType.includes('decimal') || dataType.includes('numeric')) {
      return 'number';
    }
    if (dataType.includes('date')) {
      return 'date';
    }
    if (dataType.includes('datetime')) {
      return 'datetime-local';
    }
    return 'text';
  };

  // Always paginate from filteredData
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  return (
    <div className="table-manager">
      <div className="table-header">
        <h2>{displayName}</h2>
        <div className="table-info">
          <span>Schema: {schema}.{tableName}</span>
          <span>Total Records: {allData.length}</span>
          {searchTerm && <span>Filtered: {filteredData.length}</span>}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      <div className="table-controls">
        <div className="search-box">
          <select
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="column-select"
          >
            <option value="all">All Columns</option>
            {columns.map(col => (
              <option key={col.COLUMN_NAME} value={col.COLUMN_NAME}>
                {col.COLUMN_NAME}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder={`Search in ${filterColumn === 'all' ? 'all columns' : filterColumn}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => {
                setSearchTerm('');
                setFilterColumn('all');
              }}
            >
              Clear
            </button>
          )}
          {searchTerm && (
            <span className="search-results">
              Found {filteredData.length} results in {filterColumn === 'all' ? 'all columns' : filterColumn}
            </span>
          )}
        </div>
        <div className="table-actions">
          {canModify && (
            <button
              className="btn btn-primary"
              onClick={() => setShowNewRow(!showNewRow)}
            >
              {showNewRow ? 'Cancel' : 'Add New Record'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => {
              fetchAllData();
              fetchCount();
              setSearchTerm('');
              setFilterColumn('all');
              setSortColumn(null);
              setSortDirection('asc');
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.filter(col => !actualHiddenColumns.map(h => h.toLowerCase()).includes(col.COLUMN_NAME.toLowerCase())).map((col) => (
                    <th
                      key={col.COLUMN_NAME}
                      onClick={(col.COLUMN_NAME === 'SurveyTitle' || col.COLUMN_NAME === 'VisitTypeDisplayName') ? undefined : () => handleSort(col.COLUMN_NAME)}
                      className={(col.COLUMN_NAME === 'SurveyTitle' || col.COLUMN_NAME === 'VisitTypeDisplayName') ? '' : 'sortable-header'}
                      title={(col.COLUMN_NAME === 'SurveyTitle' || col.COLUMN_NAME === 'VisitTypeDisplayName') ? undefined : 'Click to sort'}
                    >
                      <div className="header-content">
                        <span>
                          {col.COLUMN_NAME}
                          {col.IS_PRIMARY_KEY === 'YES' && <span className="pk-badge">PK</span>}
                          {col.IS_IDENTITY === 1 && <span className="identity-badge">ID</span>}
                        </span>
                        {col.COLUMN_NAME !== 'SurveyTitle' && col.COLUMN_NAME !== 'VisitTypeDisplayName' && <span className="sort-icon">{getSortIcon(col.COLUMN_NAME)}</span>}
                      </div>
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showNewRow && (
                  <tr className="new-row">
                    {columns.filter(col => !hiddenColumns.includes(col.COLUMN_NAME.toLowerCase())).map((col) => (
                      <td key={col.COLUMN_NAME}>
                        {renderNewRowCell(col)}
                      </td>
                    ))}
                    <td>
                      <button
                        className="btn btn-small btn-success"
                        onClick={handleCreate}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-small btn-cancel"
                        onClick={() => {
                          setShowNewRow(false);
                          setNewRow({});
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {paginatedData.map((row, index) => {
                  const isEditing = editingRow && getPrimaryKeyString(editingRow) === getPrimaryKeyString(row);
                  return (
                    <tr key={index}>
                      {columns.filter(col => !hiddenColumns.includes(col.COLUMN_NAME.toLowerCase())).map((col) => (
                        <td key={col.COLUMN_NAME}>
                          {renderCellValue(row, col)}
                        </td>
                      ))}
                      <td>
                        {canModify && (
                          <>
                            {isEditing ? (
                              <>
                                <button
                                  className="btn btn-small btn-success"
                                  onClick={() => handleUpdate(editingRow)}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-small btn-cancel"
                                  onClick={() => setEditingRow(null)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-small btn-edit"
                                  onClick={() => setEditingRow(row)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-small btn-danger"
                                  onClick={() => handleDelete(row)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span>
                Page {currentPage + 1} of {totalPages}
                {searchTerm && ` (Filtered)`}
              </span>
              <button
                disabled={currentPage === totalPages - 1 || (!!searchTerm && currentPage >= totalPages - 1)}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TableManager;